import { useCallback, useEffect, useState } from "react";
import { Eye, Loader2, Mail, Send, Users } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { PreviewEmailDialog } from "./PreviewEmailDialog";

const pill = (on) => `rounded-full px-2 py-0.5 text-[10px] font-semibold ${on ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`;

function SettingRow({ icon: Icon, title, hint, on, testid, onChange, children }) {
  return (
    <div className="flex items-start gap-4 border-b border-slate-100 px-5 py-4 last:border-0" data-testid={testid}>
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600"><Icon className="h-4 w-4" /></div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">{title}
          {onChange && <span className={pill(on)} data-testid={`${testid}-state`}>{on ? "ON" : "OFF"}</span>}</div>
        <p className="mt-0.5 max-w-2xl text-xs text-slate-500">{hint}</p>
        {children}
      </div>
      {onChange && <Switch checked={on} onCheckedChange={onChange} data-testid={`${testid}-toggle`} />}
    </div>
  );
}

/** Price List app → Settings tab: go-live switches (super admin), staff email copies (approvers), email preview. */
export function PriceListSettings() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/price-list/admin").then(({ data }) => setD(data))
    .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load the settings")), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Loader2 className="mx-auto mt-12 h-6 w-6 animate-spin text-slate-300" />;

  const save = async (body, msg) => {
    try { await api.put("/price-list/config", body); toast.success(msg); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"); }
  };
  const flip = (key, on, label, ask) => window.confirm(ask) && save({ [key]: on }, `${label} turned ${on ? "on" : "off"}`);
  return (
    <div className="max-w-4xl space-y-4" data-testid="price-list-settings">
      {d.is_super && (
        <Card className="overflow-hidden border-slate-200 shadow-sm" data-testid="price-launch-switches">
          <div className="border-b border-slate-100 bg-amber-50/60 px-5 py-3">
            <h3 className="font-heading text-sm font-semibold text-slate-800">Go-live switches</h3>
            <p className="text-xs text-slate-500">Super admins only. Both start OFF.</p>
          </div>
          <SettingRow icon={Eye} title="Partner Price List visible" testid="price-partner-visible" on={d.partner_visible}
            hint="When off, partners get no dashboard card, PDF or alerts, even with Price List access. Alerts and emails for changes approved while hidden are not sent later."
            onChange={(on) => flip("partner_visible", on, "Partner Price List", on ? "Show the Price List to partners now?" : "Hide the Price List from partners?")} />
          <SettingRow icon={Mail} title="Price List emails enabled" testid="price-emails-enabled" on={d.emails_enabled}
            hint="When off, announcement, reminder, chaser, cancellation and new-product emails are held and logged, not sent."
            onChange={(on) => flip("emails_enabled", on, "Price List emails", on ? "Start sending price list emails?" : "Stop sending price list emails?")}>
            {!d.emails_enabled && <p className="mt-1 text-xs font-medium text-amber-700" data-testid="price-held-emails">{d.held_emails} email(s) held so far</p>}
          </SettingRow>
          <SettingRow icon={Send} title="Preview a partner email" testid="price-preview-email"
            hint="Sends the exact partner email to your own address, marked [PREVIEW]. Works while emails are off.">
            <div className="mt-2"><PreviewEmailDialog d={d} /></div>
          </SettingRow>
        </Card>
      )}
      {d.can.approve && (
        <Card className="overflow-hidden border-slate-200 shadow-sm" data-testid="price-notification-settings">
          <div className="border-b border-slate-100 px-5 py-3">
            <h3 className="font-heading text-sm font-semibold text-slate-800">Notifications</h3>
          </div>
          <SettingRow icon={Users} title="Email copies to staff" testid="price-staff-copies" on={d.staff_copies}
            hint="Copies of announcement, reminder, chaser and cancellation emails go to partner-team staff and super admins."
            onChange={(on) => save({ staff_copies: on }, on ? "Staff copies turned on" : "Staff copies turned off")} />
        </Card>
      )}
    </div>
  );
}
